import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { CodeNameTag } from '../components/CodeNameTag';
import {
  Upload, Search, Filter, FolderOpen, TrendingUp,
  AlertTriangle, CheckCircle, Clock, Trash2, Plus, AlertCircle, Globe, ArrowUpDown, Star, Bookmark
} from 'lucide-react';
import { STAGE_LABELS, STAGE_ORDER, PRE_REVIEW_STAGES } from '../lib/stages';

// Live Deals only shows the pipeline from OLTA Approved onwards.
const LIVE_MIN_STAGE_IDX = STAGE_ORDER.indexOf('olta_approved');

export default function DashboardPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAdmin, isAcMember } = useAuth();
  // Restore the last Live Deals search/filter state (persisted across navigation).
  const savedRef = useRef(null);
  if (savedRef.current === null) {
    try { savedRef.current = JSON.parse(sessionStorage.getItem('olta-livedeals-search')) || {}; }
    catch { savedRef.current = {}; }
  }
  const saved = savedRef.current;
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [statusFilter, setStatusFilter] = useState(saved.statusFilter ?? 'all');
  const [stageFilter, setStageFilter] = useState(() => {
    const s = saved.stageFilter ?? 'all';
    return (s !== 'all' && s !== 'pending_approvals' && STAGE_ORDER.indexOf(s) < LIVE_MIN_STAGE_IDX) ? 'all' : s;
  });
  const [ownerFilter, setOwnerFilter] = useState(saved.ownerFilter ?? 'all');
  const [firstScreenFilter, setFirstScreenFilter] = useState(saved.firstScreenFilter ?? 'all');
  const [successionFilter, setSuccessionFilter] = useState(saved.successionFilter ?? 'all');
  const [infraFilter, setInfraFilter] = useState(saved.infraFilter ?? 'all');
  const [favFilter, setFavFilter] = useState(saved.favFilter ?? (searchParams.get('favorites') === 'true'));
  const [favUser, setFavUser] = useState(saved.favUser ?? 'me'); // 'me' or user_id
  const [teamFavs, setTeamFavs] = useState([]);
  const [searchQuery, setSearchQuery] = useState(saved.searchQuery ?? '');
  const [sortBy, setSortBy] = useState(saved.sortBy ?? null); // 'phase1' | 'phase2' | null
  const [sortDir, setSortDir] = useState(saved.sortDir ?? 'desc'); // 'asc' | 'desc'
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [projectName, setProjectName] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [dealType, setDealType] = useState('brokered');
  const [companyWebsite, setCompanyWebsite] = useState('');
  const [dealStats, setDealStats] = useState(null);
  const [teamMembers, setTeamMembers] = useState([]);

  const fetchStats = useCallback(() => {
    api.getProjectStats().then(r => setDealStats(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    fetchStats();
    api.getTeamFavorites().then(r => setTeamFavs(r.data || [])).catch(() => {});
    api.listTeam().then(r => setTeamMembers(r.data || [])).catch(() => {});
  }, [fetchStats]);

  const fetchProjects = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.listProjects('all');
      setProjects(res.data);
      setHasSearched(true);
      fetchStats();
      // Load team favorites
      api.getTeamFavorites().then(r => setTeamFavs(r.data || [])).catch(() => {});
    } catch (err) {
      toast.error('Failed to load projects');
    } finally {
      setLoading(false);
    }
  }, [fetchStats]);

  // Persist filter/search state so it survives navigating to a deal and back.
  useEffect(() => {
    const state = { statusFilter, stageFilter, ownerFilter, firstScreenFilter, successionFilter,
      infraFilter, favFilter, favUser, searchQuery, sortBy, sortDir, hasSearched };
    try { sessionStorage.setItem('olta-livedeals-search', JSON.stringify(state)); } catch { /* ignore */ }
  }, [statusFilter, stageFilter, ownerFilter, firstScreenFilter, successionFilter, infraFilter, favFilter, favUser, searchQuery, sortBy, sortDir, hasSearched]);

  // If the user had already searched before leaving, restore the results on return.
  useEffect(() => {
    if (saved.hasSearched) fetchProjects();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!projectName.trim()) return;
    setUploadLoading(true);
    try {
      if (selectedFile) {
        // Chunked upload flow (brokered with file)
        const CHUNK_SIZE = 512 * 1024;
        const totalChunks = Math.ceil(selectedFile.size / CHUNK_SIZE);
        const initRes = await api.initChunkedProject(selectedFile.name, totalChunks);
        const uploadId = initRes.data.upload_id;
        for (let i = 0; i < totalChunks; i++) {
          const start = i * CHUNK_SIZE;
          const end = Math.min(start + CHUNK_SIZE, selectedFile.size);
          const chunk = selectedFile.slice(start, end);
          let attempts = 0;
          while (attempts < 3) {
            try { await api.uploadChunk(uploadId, i, chunk); break; }
            catch (chunkErr) {
              attempts++;
              if (attempts >= 3) throw chunkErr;
              await new Promise(r => setTimeout(r, 1000 * attempts));
            }
          }
        }
        await api.completeChunkedProject(uploadId, projectName.trim(), dealType, companyWebsite.trim());
      } else {
        // Simple create (no file — proprietary or brokered without doc)
        await api.createProjectSimple(projectName.trim(), dealType, companyWebsite.trim());
      }
      toast.success('Project created');
      setUploadOpen(false);
      setProjectName(''); setSelectedFile(null);
      setDealType('brokered'); setCompanyWebsite('');
      fetchProjects();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create project');
    } finally {
      setUploadLoading(false);
    }
  };

  const [deleteDialog, setDeleteDialog] = useState(null); // { id, name }

  const handleDelete = async () => {
    if (!deleteDialog) return;
    try {
      await api.deleteProject(deleteDialog.id);
      toast.success('Project deleted');
      setDeleteDialog(null);
      fetchProjects();
    } catch {
      toast.error('Failed to delete project');
    }
  };

  const filtered = projects.filter(p => {
    const matchSearch = !searchQuery || p.name.toLowerCase().includes(searchQuery.toLowerCase()) || (p.random_project_name || '').toLowerCase().includes(searchQuery.toLowerCase());
    const matchStage = stageFilter === 'all' || stageFilter === 'pending_approvals'
      ? true
      : p.pipeline_stage === stageFilter;
    const matchApprovals = stageFilter === 'pending_approvals' ? (!!p.pass_proposed || !!p.contact_proposed || !!p.approval_proposed) : true;
    const matchOwner = ownerFilter === 'all' || (ownerFilter === 'unassigned' ? !p.owner_name : p.owner_name === ownerFilter);
    const matchFirstScreen = firstScreenFilter === 'all' || (firstScreenFilter === 'complete' ? p.first_screen_complete : !p.first_screen_complete);
    const matchSuccession = successionFilter === 'all' || p.succession_case === successionFilter;
    const matchInfra = infraFilter === 'all' || p.infrastructure_adjacent === infraFilter;
    const matchFav = !favFilter || (() => {
      if (favUser === 'me') return p.is_favorite;
      const userFavs = teamFavs.find(f => f.user_id === favUser);
      return userFavs ? userFavs.project_ids.includes(p.id) : false;
    })();
    // Hide passed deals unless favorites filter is active or the user explicitly filters for Passed
    const matchNotPassed = favFilter || stageFilter === 'passed' || p.pipeline_stage !== 'passed';
    // Live Deals shows only OLTA Approved and later — earlier stages live in the Deal Explorer
    const matchLive = favFilter || stageFilter !== 'all' || STAGE_ORDER.indexOf(p.pipeline_stage) >= LIVE_MIN_STAGE_IDX;
    return matchSearch && matchStage && matchApprovals && matchOwner && matchFirstScreen && matchSuccession && matchInfra && matchFav && matchNotPassed && matchLive;
  });

  // Sort
  const sorted = [...filtered].sort((a, b) => {
    if (!sortBy) return 0;
    const getScore = (p) => {
      if (sortBy === 'phase1') return p.phase1_max ? (p.phase1_score / p.phase1_max) : -1;
      if (sortBy === 'phase2') return p.phase2_max ? (p.phase2_score / p.phase2_max) : -1;
      return 0;
    };
    const diff = getScore(a) - getScore(b);
    return sortDir === 'asc' ? diff : -diff;
  });

  const uniqueOwners = [...new Set(projects.map(p => p.owner_name).filter(Boolean))];
  // "Claimed by" options come from team members (so the filter is usable before searching),
  // unioned with any owner names already present in the loaded results.
  const ownerOptions = [...new Set([
    ...teamMembers.map(m => m.name).filter(Boolean),
    ...uniqueOwners,
  ])].sort((a, b) => a.localeCompare(b));

  const stats = dealStats || { total: 0, pending_first_screen: 0, olta_reviewed: 0, olta_approved: 0, pending_approvals: 0 };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'ko': return <span className="badge-ko rounded-sm" data-testid="status-badge-ko">K.O.</span>;
      case 'passed': return <span className="badge-pass rounded-sm" data-testid="status-badge-passed">PASSED</span>;
      case 'missing_info': return <span className="badge-pending rounded-sm" data-testid="status-badge-missing" style={{ background: 'rgba(217,119,6,0.2)', color: '#f59e0b', borderColor: 'rgba(217,119,6,0.4)' }}>MISSING INFO</span>;
      default: return <span className="badge-pending rounded-sm" data-testid="status-badge-pending">PENDING</span>;
    }
  };

  function scoreColor(pct) {
    if (pct >= 70) return 'text-green-400';
    if (pct >= 40) return 'text-yellow-400';
    return 'text-red-400';
  }

  const getScoreDisplay = (score, max) => {
    if (score === null || score === undefined) return <span className="text-muted-foreground text-xs">--</span>;
    const pct = Math.round((score / max) * 100);
    return <span className={`font-mono text-sm ${scoreColor(pct)}`}>{score}/{max} <span className="text-xs text-muted-foreground">({pct}%)</span></span>;
  };

  return (
    <div className="space-y-6 animate-fade-in-up" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl lg:text-4xl text-foreground tracking-tight">Live Deals</h1>
          <p className="text-muted-foreground text-xs sm:text-sm mt-1 hidden sm:block">Pipeline from OLTA Approved onwards — earlier stages live in the Deal Explorer, passed deals in the Deal Funnel</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {!isAcMember && (
          <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
          <DialogTrigger asChild>
            <Button
              data-testid="upload-project-button"
              className="bg-blue-600 hover:bg-blue-500 text-white rounded-sm font-medium text-sm h-9 px-4 transition-colors duration-100"
            >
              <Plus className="w-4 h-4 mr-1.5" /> New Project
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-card border-border rounded-sm max-w-md" data-testid="upload-dialog">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl text-foreground">New Deal</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleUpload} className="space-y-4 mt-2">
              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground uppercase tracking-wider">Project Name</Label>
                <Input
                  data-testid="project-name-input"
                  value={projectName}
                  onChange={(e) => setProjectName(e.target.value)}
                  placeholder="e.g., Project Alpha"
                  className="bg-secondary border-border text-foreground rounded-sm h-9 text-sm"
                  required
                />
              </div>
              {/* Deal Type Toggle */}
              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground uppercase tracking-wider">Deal Type</Label>
                <div className="flex rounded-sm border border-border overflow-hidden">
                  <button type="button" data-testid="deal-type-brokered"
                    onClick={() => setDealType('brokered')}
                    className={`flex-1 py-2 text-sm font-medium transition-colors ${dealType === 'brokered' ? 'bg-blue-600/20 text-blue-400 border-r border-blue-600/30' : 'bg-secondary text-muted-foreground border-r border-border hover:text-foreground'}`}>
                    Brokered
                  </button>
                  <button type="button" data-testid="deal-type-proprietary"
                    onClick={() => setDealType('proprietary')}
                    className={`flex-1 py-2 text-sm font-medium transition-colors ${dealType === 'proprietary' ? 'bg-purple-600/20 text-purple-400' : 'bg-secondary text-muted-foreground hover:text-foreground'}`}>
                    Proprietary
                  </button>
                </div>
              </div>
              {/* Website URL — shown for proprietary, optional for brokered */}
              {dealType === 'proprietary' && (
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground uppercase tracking-wider">Company Website</Label>
                  <div className="relative">
                    <Globe className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
                    <Input
                      data-testid="company-website-input"
                      value={companyWebsite}
                      onChange={(e) => setCompanyWebsite(e.target.value)}
                      placeholder="e.g., www.company.de"
                      className="bg-secondary border-border text-foreground rounded-sm h-9 text-sm pl-8"
                    />
                  </div>
                  <p className="text-[10px] text-muted-foreground">The website will be scraped to populate deal sections</p>
                </div>
              )}
              {/* File Upload — optional */}
              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground uppercase tracking-wider">
                  {dealType === 'brokered' ? 'Teaser / CIM (optional)' : 'Attach Document (optional)'}
                </Label>
                <div className="border border-dashed border-border rounded-sm p-4 text-center hover:border-muted-foreground/50 transition-colors">
                  <input
                    type="file"
                    data-testid="file-upload-input"
                    onChange={(e) => setSelectedFile(e.target.files[0])}
                    className="hidden"
                    id="pdf-upload"
                  />
                  <label htmlFor="pdf-upload" className="cursor-pointer">
                    <Upload className="w-6 h-6 text-muted-foreground mx-auto mb-1.5" />
                    <p className="text-sm text-muted-foreground">
                      {selectedFile ? selectedFile.name : 'Click to select file'}
                    </p>
                  </label>
                </div>
              </div>
              <Button
                type="submit"
                data-testid="submit-upload-button"
                disabled={uploadLoading || !projectName.trim()}
                className="w-full bg-blue-600 hover:bg-blue-500 text-white rounded-sm font-medium h-9 text-sm transition-colors duration-100"
              >
                {uploadLoading ? (
                  <span className="flex items-center gap-2">
                    <span className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    {selectedFile ? 'Uploading...' : 'Creating...'}
                  </span>
                ) : 'Create Project'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3" data-testid="dashboard-stats">
        {[
          { label: 'Total Live', value: dealStats ? stats.total.toLocaleString() : '--', icon: FolderOpen, color: 'text-blue-400' },
          { label: 'Pending First Screen', value: dealStats ? stats.pending_first_screen.toLocaleString() : '--', icon: Clock, color: 'text-amber-400' },
          { label: 'Under OLTA Review', value: dealStats ? stats.olta_reviewed.toLocaleString() : '--', icon: CheckCircle, color: 'text-emerald-400' },
          { label: 'OLTA Approved', value: dealStats ? stats.olta_approved.toLocaleString() : '--', icon: TrendingUp, color: 'text-green-400' },
          { label: 'Pending Approvals', value: dealStats ? stats.pending_approvals.toLocaleString() : '--', icon: AlertCircle, color: 'text-amber-400' },
        ].map(s => (
          <div key={s.label} className="glass-card card-depth rounded-sm p-3">
            <div className="flex items-center gap-2 mb-1">
              <s.icon className={`w-3.5 h-3.5 ${s.color}`} strokeWidth={1.5} />
              <span className="text-xs text-muted-foreground uppercase tracking-wider">{s.label}</span>
            </div>
            <p className={`font-mono text-2xl font-medium count-animate ${s.color}`}>{s.value}</p>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3">
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
          <Input
            data-testid="search-projects-input"
            placeholder="Search projects, code names..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-8 bg-secondary border-border text-foreground rounded-sm h-8 text-sm"
          />
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Filter className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
          <Select value={stageFilter} onValueChange={setStageFilter}>
            <SelectTrigger className="w-36 bg-secondary border-border text-foreground rounded-sm h-8 text-xs"><SelectValue placeholder="Stage" /></SelectTrigger>
            <SelectContent className="bg-card border-border rounded-sm">
              <SelectItem value="all">Stage: All</SelectItem>
              <SelectItem value="pending_approvals" className="text-amber-400">Pending Approvals</SelectItem>
              {STAGE_ORDER.slice(LIVE_MIN_STAGE_IDX).map(s => <SelectItem key={s} value={s}>{STAGE_LABELS[s]}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={ownerFilter} onValueChange={setOwnerFilter}>
            <SelectTrigger className="w-36 bg-secondary border-border text-foreground rounded-sm h-8 text-xs" data-testid="claimed-by-filter"><SelectValue placeholder="Claimed by" /></SelectTrigger>
            <SelectContent className="bg-card border-border rounded-sm">
              <SelectItem value="all">Claimed by: All</SelectItem>
              <SelectItem value="unassigned">Unclaimed</SelectItem>
              {ownerOptions.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={firstScreenFilter} onValueChange={setFirstScreenFilter}>
            <SelectTrigger className="w-32 bg-secondary border-border text-foreground rounded-sm h-8 text-xs"><SelectValue placeholder="First Screen" /></SelectTrigger>
            <SelectContent className="bg-card border-border rounded-sm">
              <SelectItem value="all">First Screen: All</SelectItem>
              <SelectItem value="complete">Complete</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
            </SelectContent>
          </Select>
          <Select value={successionFilter} onValueChange={setSuccessionFilter}>
            <SelectTrigger className="w-36 bg-secondary border-border text-foreground rounded-sm h-8 text-xs"><SelectValue placeholder="Succession" /></SelectTrigger>
            <SelectContent className="bg-card border-border rounded-sm">
              <SelectItem value="all">Succession: All</SelectItem>
              <SelectItem value="yes">Yes</SelectItem>
              <SelectItem value="maybe">Maybe</SelectItem>
              <SelectItem value="no">No</SelectItem>
            </SelectContent>
          </Select>
          <Select value={infraFilter} onValueChange={setInfraFilter}>
            <SelectTrigger className="w-36 bg-secondary border-border text-foreground rounded-sm h-8 text-xs"><SelectValue placeholder="Infra" /></SelectTrigger>
            <SelectContent className="bg-card border-border rounded-sm">
              <SelectItem value="all">Infra: All</SelectItem>
              <SelectItem value="fit">Fit</SelectItem>
              <SelectItem value="fringe">Fringe</SelectItem>
              <SelectItem value="no_fit">No Fit</SelectItem>
            </SelectContent>
          </Select>
          <button onClick={() => { setFavFilter(!favFilter); setFavUser('me'); }} data-testid="fav-filter-btn"
            className={`flex items-center gap-1.5 px-3 h-8 rounded-sm text-xs border transition-colors ${
              favFilter && favUser === 'me' ? 'bg-amber-500/10 text-amber-400 border-amber-500/30' : 'bg-secondary border-border text-muted-foreground hover:text-foreground'
            }`}>
            <Star className="w-3 h-3" fill={favFilter ? 'currentColor' : 'none'} /> My Favorites
          </button>
          <Select value={favFilter && favUser !== 'me' ? favUser : 'none'} onValueChange={v => {
            if (v === 'none') { setFavFilter(false); setFavUser('me'); }
            else { setFavFilter(true); setFavUser(v); }
          }}>
            <SelectTrigger className="w-36 bg-secondary border-border rounded-sm h-8 text-xs"><SelectValue placeholder="Team Favorites" /></SelectTrigger>
            <SelectContent className="bg-card border-border rounded-sm">
              <SelectItem value="none" className="text-xs">Team Favorites</SelectItem>
              {teamFavs.map(f => (
                <SelectItem key={f.user_id} value={f.user_id} className="text-xs">{f.name} ({f.count})</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            data-testid="search-deals-btn"
            onClick={fetchProjects}
            disabled={loading}
            className="bg-blue-600 hover:bg-blue-500 text-white rounded-sm font-medium text-xs h-8 px-5 transition-colors duration-100 ml-1"
          >
            {loading ? (
              <span className="flex items-center gap-1.5">
                <span className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                Loading...
              </span>
            ) : (
              <><Search className="w-3.5 h-3.5 mr-1.5" /> Search</>
            )}
          </Button>
        </div>
      </div>

      {/* Project Table */}
      {!hasSearched ? (
        <div className="text-center py-24 border border-border rounded-sm bg-card" data-testid="initial-state">
          <Search className="w-10 h-10 text-muted-foreground/40 mx-auto mb-4" strokeWidth={1.5} />
          <p className="text-foreground text-sm font-medium mb-1">Set your criteria and hit Search</p>
          <p className="text-muted-foreground/60 text-xs">Use the filters above to narrow down deals, then click the Search button to load results.</p>
        </div>
      ) : loading ? (
        <div className="flex items-center justify-center py-20">
          <span className="w-6 h-6 border-2 border-blue-600/30 border-t-blue-600 rounded-full animate-spin" />
        </div>
      ) : sorted.length === 0 ? (
        <div className="text-center py-20 border border-border rounded-sm bg-card" data-testid="empty-state">
          <FolderOpen className="w-10 h-10 text-muted-foreground mx-auto mb-3" strokeWidth={1.5} />
          <p className="text-muted-foreground text-sm">No projects found</p>
          <p className="text-muted-foreground/60 text-xs mt-1">Create a new project to get started</p>
        </div>
      ) : (
        <div className="border border-border rounded-sm overflow-x-auto" data-testid="projects-table">
          <table className="w-full min-w-[700px]">
            <thead>
              <tr className="bg-secondary/50 text-xs uppercase tracking-wider text-muted-foreground font-bold">
                <th className="text-left py-2.5 px-4">Project</th>
                <th className="text-left py-2.5 px-4">Owner</th>
                <th className="text-left py-2.5 px-4">Stage</th>
                <th className="text-left py-2.5 px-4">First Screen</th>
                <th className="text-left py-2.5 px-4">
                  <button onClick={() => { setSortBy(sortBy === 'phase1' ? null : 'phase1'); setSortDir(sortBy === 'phase1' && sortDir === 'desc' ? 'asc' : 'desc'); }}
                    className="flex items-center gap-1 hover:text-foreground transition-colors">
                    Phase 1 <ArrowUpDown className={`w-3 h-3 ${sortBy === 'phase1' ? 'text-blue-400' : 'text-muted-foreground/40'}`} />
                  </button>
                </th>
                <th className="text-left py-2.5 px-4">
                  <button onClick={() => { setSortBy(sortBy === 'phase2' ? null : 'phase2'); setSortDir(sortBy === 'phase2' && sortDir === 'desc' ? 'asc' : 'desc'); }}
                    className="flex items-center gap-1 hover:text-foreground transition-colors">
                    Phase 2 <ArrowUpDown className={`w-3 h-3 ${sortBy === 'phase2' ? 'text-blue-400' : 'text-muted-foreground/40'}`} />
                  </button>
                </th>
                <th className="text-right py-2.5 px-4"></th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((p, i) => {
                const preReview = PRE_REVIEW_STAGES.includes(p.pipeline_stage);
                const firstScreenStatus = p.first_screen_complete
                  ? <span className="text-[10px] font-mono text-emerald-400 bg-emerald-900/20 border border-emerald-900/30 px-1.5 py-0.5 rounded-sm">COMPLETE</span>
                  : preReview
                    ? <span className="text-[10px] font-mono text-amber-400 bg-amber-900/20 border border-amber-900/30 px-1.5 py-0.5 rounded-sm">PENDING</span>
                    : <span className="text-[10px] font-mono text-emerald-400 bg-emerald-900/20 border border-emerald-900/30 px-1.5 py-0.5 rounded-sm">COMPLETE</span>;
                return (
                <tr
                  key={p.id}
                  data-testid={`project-row-${i}`}
                  onClick={() => navigate(`/projects/${p.id}`)}
                  className="border-t border-border even:bg-secondary/40 row-hover-glow cursor-pointer"
                  style={{ animationDelay: `${i * 30}ms` }}
                >
                  <td className="py-3 px-4">
                    <div className="flex items-center gap-2">
                      <button onClick={(e) => {
                        e.stopPropagation();
                        api.toggleFavorite(p.id).then(() => fetchProjects()).catch(() => {});
                      }} data-testid={`favorite-toggle-${i}`} className={`shrink-0 transition-colors ${p.is_favorite ? 'text-amber-400' : 'text-muted-foreground/20 hover:text-amber-400/50'}`}>
                        <Star className="w-3.5 h-3.5" fill={p.is_favorite ? 'currentColor' : 'none'} />
                      </button>
                      <button onClick={(e) => {
                        e.stopPropagation();
                        api.toggleBookmark(p.id).then(() => fetchProjects()).catch(() => {});
                      }} data-testid={`bookmark-toggle-${i}`} className={`shrink-0 transition-colors ${p.is_bookmarked ? 'text-blue-400' : 'text-muted-foreground/20 hover:text-blue-400/50'}`} title={p.is_bookmarked ? 'Remove bookmark' : 'Bookmark (private)'}>
                        <Bookmark className="w-3.5 h-3.5" fill={p.is_bookmarked ? 'currentColor' : 'none'} />
                      </button>
                      <span className="text-sm font-medium text-foreground">{p.name}</span>
                      {p.random_project_name && <CodeNameTag name={p.random_project_name} prefix="· " className="text-[10px] text-blue-400/70 font-mono" testId={`live-code-name-${i}`} />}
                    </div>
                  </td>
                  <td className="py-3 px-4">
                    <span className="text-xs text-muted-foreground">{p.owner_name || <span className="text-foreground/20">—</span>}</span>
                  </td>
                  <td className="py-3 px-4">
                    <span className="text-xs text-muted-foreground">{STAGE_LABELS[p.pipeline_stage] || p.pipeline_stage}</span>
                    {p.pass_proposed && <span className="ml-1.5 text-[9px] font-mono text-red-400 border border-red-800/30 bg-red-900/10 rounded px-1 py-0.5">PASS PROPOSED</span>}
                    {p.contact_proposed && <span className="ml-1.5 text-[9px] font-mono text-blue-400 border border-blue-800/30 bg-blue-900/10 rounded px-1 py-0.5">CONTACT PROPOSED</span>}
                    {p.approval_proposed && <span className="ml-1.5 text-[9px] font-mono text-blue-400 border border-blue-800/30 bg-blue-900/10 rounded px-1 py-0.5">APPROVAL PROPOSED</span>}
                  </td>
                  <td className="py-3 px-4">{firstScreenStatus}</td>
                  <td className="py-3 px-4">{getScoreDisplay(p.phase1_score, p.phase1_max)}</td>
                  <td className="py-3 px-4">{getScoreDisplay(p.phase2_score, p.phase2_max)}</td>
                  <td className="py-3 px-4 text-right">
                    {isAdmin && !isAcMember && (
                    <Button
                      variant="ghost"
                      size="sm"
                      data-testid={`delete-project-${i}`}
                      onClick={(e) => { e.stopPropagation(); setDeleteDialog({ id: p.id, name: p.name }); }}
                      className="h-7 w-7 p-0 text-muted-foreground hover:text-red-400 hover:bg-red-950/30"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                    )}
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteDialog} onOpenChange={(open) => { if (!open) setDeleteDialog(null); }}>
        <DialogContent className="bg-card border-border rounded-sm max-w-sm" data-testid="delete-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg text-foreground flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-400" /> Delete Project
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <p className="text-sm text-muted-foreground">
              Are you sure you want to permanently delete <span className="text-foreground font-medium">"{deleteDialog?.name}"</span>?
            </p>
            <p className="text-xs text-red-400/70">This will remove all analysis data, scores, notes, and the uploaded document. This action cannot be undone.</p>
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setDeleteDialog(null)} data-testid="cancel-delete-button"
                className="text-muted-foreground rounded-sm text-xs h-8 px-3">Cancel</Button>
              <Button onClick={handleDelete} data-testid="confirm-delete-button"
                className="bg-red-700 hover:bg-red-600 text-white rounded-sm font-medium text-xs h-8 px-3">
                <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete Permanently
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
